<template>
  <div id="app" class="has-navbar-fixed-top">
    <div v-if="!isAppLoading" class="app-content-wrapper">
      <side-navigation v-if="doesRouteHaveSideNavigation" />
      <div
        :class="[
          'main-wrapper',
          { 'has-side-navigation': doesRouteHaveSideNavigation },
        ]"
      >
        <app-header v-if="doesRouteHaveAppHeader" :doesRouteHaveSideNavigation="doesRouteHaveSideNavigation" />
        <main>
          <upgrade-subscription-modal
            :is-modal-active="isSubscriptionModalActive"
            :workspace-user-count="activeWorkspace && activeWorkspace.userCount ? activeWorkspace.userCount : 0"
            :can-cancel="false"
          />
          <router-view @loginSuccess="subscribeEventListeners" />
        </main>
      </div>
    </div>
    <b-loading is-full-page :active.sync="isAppLoading" :can-cancel="false"></b-loading>
  </div>
</template>

<script>
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
import * as AuthActions from '@/store/actions/Auth.actions';
import * as WorkspaceActions from '@/store/actions/Workspace.actions';
import * as SubscriptionActions from '@/store/actions/Subscription.actions';
import * as SubscriptionMutations from '@/store/mutations/Subscription.mutations';
import * as WorkspaceMutations from '@/store/mutations/Workspace.mutations';
import * as UserMutations from '@/store/mutations/User.mutations';
import * as DatabaseService from '@/services/Database.service';
import * as collections from '@/constants/dbCollections';
import * as SubscriptionTypes from '@/constants/Subscription.types';
import { isObjectEmpty, documentWithId } from '@/helpers/dataHelpers';
import UpgradeSubscriptionModal from '@/components/UpgradeSubscriptionModal.vue';
import AppHeader from '@/components/AppHeader.vue';
import SideNavigation from '@/components/SideNavigation.vue';

export default {
  name: 'App',

  components: {
    AppHeader,
    SideNavigation,
    UpgradeSubscriptionModal,
  },

  data() {
    return {
      isSessionLoading: true,
      isWorkspaceDataLoading: true,
      unsubscribeWorkspaceUsersListener: null,
      unsubscribeWorkspaceSubscriptionListener: null,
      unbsubscribeToLoggedInUserChangeEvents: null,
      systemPrefersDark: window.matchMedia('(prefers-color-scheme: dark)').matches,
      systemThemeMediaQuery: null,
    };
  },

  computed: {
    ...mapState({
      isSessionAuthLoading: state => state.auth.isSessionAuthLoading,
      isGoogleAuthLoading: state => state.auth.isGoogleAuthLoading,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
      loggedInUserId: state => state.auth.loggedInUserId,
      isUserLoggingOut: state => state.auth.isUserLoggingOut,
      subscriptions: state => state.subscription.subscriptions,
    }),
    ...mapGetters([
      'loggedInUser',
      'activeWorkspace',
      'activeWorkspaceSubscription',
    ]),
    doesRouteHaveSideNavigation() {
      return this.$route.meta.hasSideNavigation;
    },
    doesRouteHaveAppHeader() {
      return !(this.$route.path.includes('sign-up') && this.$route.params.invitationId);
    },
    isAppLoading() {
      return (
        this.isGoogleAuthLoading
        || this.isSessionAuthLoading
        || this.isWorkspaceDataLoading
        || this.isUserLoggingOut
      );
    },
    resolvedTheme() {
      const pref = this.loggedInUser && this.loggedInUser.themePreference;
      if (pref === 'dark') return 'dark';
      if (pref === 'light') return 'light';
      return this.systemPrefersDark ? 'dark' : 'light';
    },
    isSubscriptionModalActive() {
      return (
        this.activeWorkspace
        && this.activeWorkspace.userCount
        && this.activeWorkspace.userCount > 5
        && this.activeWorkspaceSubscription
        && this.activeWorkspaceSubscription.stripePricingPlan === SubscriptionTypes.STRIPE_PRICE_TYPE_FREE_FOREVER(this.$env)
        && !this.activeWorkspaceSubscription.isExempt
      );
    },
  },

  created() {
    this.initTheme();
    this.getInitialState();
  },

  beforeDestroy() {
    if (this.systemThemeMediaQuery) {
      this.systemThemeMediaQuery.removeEventListener('change', this.handleSystemThemeChange);
    }
  },

  watch: {
    resolvedTheme(theme) {
      document.documentElement.setAttribute('data-theme', theme);
    },
  },

  beforeUpdate() {
    if (this.$route.meta.requiresAuth && !this.loggedInUserId) {
      this.$router.push('/log-in');
      this.unsubscribeEventListeners();
    }
  },

  methods: {
    ...mapActions({
      logInWithExistingSessionToken: AuthActions.LOG_IN_WITH_EXISTING_SESSION_TOKEN,
      fetchWorkspaceById: WorkspaceActions.FETCH_WORKSPACE_BY_ID,
      logOutUser: AuthActions.LOG_OUT_USER,
      fetchSubscriptionByWorkspaceId: SubscriptionActions.FETCH_SUBSCRIPTION_BY_WORKSPACE_ID,
    }),
    ...mapMutations({
      setActiveWorkspace: WorkspaceMutations.SET_ACTIVE_WORKSPACE,
      setActiveWorkspaceSubscription: SubscriptionMutations.SET_ACTIVE_WORKSPACE_SUBSCRIPTION,
    }),
    subscribeEventListeners() {
      this.subscribeToWorkspaceUsersChangeEvents();
      this.subscribeToWorkspaceSubscriptionChangeEvents();
      this.subscribeToLoggedInUserChangeEvents();
    },
    unsubscribeEventListeners() {
      this.unsubscribeWorkspaceUsersListener();
      this.unsubscribeWorkspaceSubscriptionListener();
      this.unbsubscribeToLoggedInUserChangeEvents();
    },
    async subscribeToWorkspaceUsersChangeEvents() {
      this.unsubscribeWorkspaceUsersListener = await DatabaseService
        .getDocumentReference(collections.WORKSPACE_USERS, this.loggedInUser.workspaceId)
        .onSnapshot(this.handleWorkspaceUsersUpdate);
    },
    async subscribeToWorkspaceSubscriptionChangeEvents() {
      this.unsubscribeWorkspaceSubscriptionListener = await DatabaseService
        .getDocumentReference(collections.SUBSCRIPTIONS, this.activeWorkspaceSubscription.id)
        .onSnapshot(this.handleWorkspaceSubscriptionUpdate);
    },
    async subscribeToLoggedInUserChangeEvents() {
      this.unbsubscribeToLoggedInUserChangeEvents = await DatabaseService
        .getDocumentReferenceInSubcollection(
          collections.WORKSPACE_USERS,
          collections.USERS,
          this.loggedInUser.workspaceId,
          this.loggedInUser.uid,
        )
        .onSnapshot(this.handleUserSubscriptionUpdate);
    },
    handleWorkspaceUsersUpdate(workspaceUsersSnapshot) {
      const shapedDocument = documentWithId(workspaceUsersSnapshot);
      this.$store.commit(WorkspaceMutations.SET_WORKSPACE_USER_COUNT, { userCount: shapedDocument.userCount });
    },
    handleWorkspaceSubscriptionUpdate(workspaceSubscriptionSnapshot) {
      const shapedDocument = documentWithId(workspaceSubscriptionSnapshot);
      this.$store.commit(SubscriptionMutations.RECEIVE_SUBSCRIPTIONS, { [shapedDocument.id]: { ...shapedDocument } });
    },
    handleUserSubscriptionUpdate(userSubscriptionSnapshot) {
      const shapedDocument = documentWithId(userSubscriptionSnapshot);
      this.$store.commit(UserMutations.RECEIVE_USERS, { users: { [shapedDocument.id]: { ...shapedDocument } } });
    },
    async getInitialState() {
      if (this.isObjectEmpty(this.loggedInUser)) {
        try {
          await this.logInWithExistingSessionToken();
          if (this.loggedInUser.workspaceId) {
            await this.fetchWorkspaceById({ workspaceId: this.loggedInUser.workspaceId });
            this.setActiveWorkspace({ workspaceId: this.loggedInUser.workspaceId });

            await this.fetchSubscriptionByWorkspaceId({ workspaceId: this.activeWorkspaceId });
            this.setActiveWorkspaceSubscription({ subscriptionId: Object.keys(this.subscriptions)[0] });
            this.subscribeEventListeners();
            if (this.$route.path === '/log-in') this.$router.push('/dashboard');
          } else {
            this.$router.push('/onboarding');
          }
          this.isSessionLoading = false;
          this.isWorkspaceDataLoading = false;
        } catch {
          if (this.$route.meta.requiresAuth) {
            this.logOutUser();
            this.$router.push('/log-in');
          }
          this.isSessionLoading = false;
          this.isWorkspaceDataLoading = false;
        }
      } else {
        this.isSessionLoading = false;
        this.isWorkspaceDataLoading = false;
      }
    },
    initTheme() {
      // Apply system preference immediately so there is no flash on first load
      document.documentElement.setAttribute('data-theme', this.systemPrefersDark ? 'dark' : 'light');

      // Listen for OS-level theme changes
      this.systemThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.systemThemeMediaQuery.addEventListener('change', this.handleSystemThemeChange);
    },
    handleSystemThemeChange(event) {
      this.systemPrefersDark = event.matches;
    },
    isObjectEmpty,
  },
};
</script>

<style lang="scss">
@import '@/styles/main.scss';

.has-side-navigation {
  @media screen and (min-width: $breakpoint-to-hide-side-navigation + 1) {
    margin-left: $side-navigation-width;
  }
}

.has-navbar-fixed-top {
  padding-top: 3.75rem;
}
</style>
